import dayjs, { type Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { sanitizePositiveIntegerDigits } from '@/features/instruments/domain'
import {
  parseSliderMaxField,
  parseSliderMinField,
  parseSliderStepField,
  sanitizeSliderMaxFieldInput,
  sanitizeSliderMinFieldInput,
  SLIDER_DEFAULT_MAX_VALUE,
  SLIDER_VALUE_MAX_CEILING,
  SLIDER_VALUE_MIN_FLOOR,
} from '@/features/instruments/domain/sliderRange'

dayjs.extend(customParseFormat)

export const STORAGE_DATE_FMT = 'YYYY-MM-DD'

export function dayFromStorageIso(s: string): Dayjs | null {
  const t = s.trim()
  if (!t) return null
  const d = dayjs(t, STORAGE_DATE_FMT, true)
  return d.isValid() ? d : null
}

export function storageIsoFromDay(v: Dayjs | null): string {
  if (!v?.isValid()) return ''
  return v.format(STORAGE_DATE_FMT)
}

/** Combine optional lower bound with “no past dates” (no nested ternary). */
export function pinchMinDateWithPastRestriction(minDate: Dayjs | undefined, today: Dayjs): Dayjs {
  if (minDate == null) return today
  return minDate.isAfter(today) ? minDate : today
}

/** Combine optional upper bound with “no future dates” (no nested ternary). */
export function pinchMaxDateWithFutureRestriction(maxDate: Dayjs | undefined, today: Dayjs): Dayjs {
  if (maxDate == null) return today
  return maxDate.isBefore(today) ? maxDate : today
}

/** @deprecated Use {@link sanitizeSliderMinFieldInput} — non-negative digits only. */
export function sanitizeSliderMinInput(s: string): string {
  return sanitizeSliderMinFieldInput(s)
}

/** @deprecated Use {@link sanitizeSliderMaxFieldInput} — digits capped at 100. */
export function sanitizeSliderMaxUnsigned(s: string): string {
  return sanitizeSliderMaxFieldInput(s)
}

export {
  effectiveSliderMaxValue,
  effectiveSliderMinValue,
  parseSliderMaxField,
  parseSliderMinField,
  sanitizeSliderMaxFieldInput,
  sanitizeSliderMinFieldInput,
  sliderInclusiveSpan,
} from '@/features/instruments/domain/sliderRange'

/** Snap a raw value to the nearest valid tick on [min, max]. */
export function alignSliderPreviewValue(raw: number, min: number, max: number, step: number): number {
  const k = Math.round((raw - min) / step)
  let v = min + k * step
  if (v < min) v = min
  if (v > max) v = max
  return v
}

/** @deprecated Use {@link parseSliderMinField} */
export function parseSliderMinBound(s: string): number | null {
  return parseSliderMinField(s)
}

/** @deprecated Use {@link parseSliderMaxField} */
export function parseSliderMaxBound(s: string): number | null {
  return parseSliderMaxField(s)
}

export function parseSliderStepBound(s: string): number | null {
  return parseSliderStepField(s)
}

export function bumpSliderMinString(current: string, delta: number): string {
  const parsed = parseSliderMinField(current)
  if (parsed === null) {
    return delta > 0 ? String(SLIDER_VALUE_MIN_FLOOR) : ''
  }
  const next = Math.max(SLIDER_VALUE_MIN_FLOOR, parsed + delta)
  return sanitizeSliderMinFieldInput(String(next))
}

export function bumpSliderMaxString(current: string, delta: number): string {
  if (current.trim() === '') {
    return delta > 0 ? String(SLIDER_DEFAULT_MAX_VALUE) : ''
  }
  const parsed = parseSliderMaxField(current) ?? 0
  const next = Math.max(0, Math.min(SLIDER_VALUE_MAX_CEILING, parsed + delta))
  return sanitizeSliderMaxFieldInput(String(next))
}

export function bumpSliderStepString(current: string, delta: number): string {
  const t = current.trim()
  if (t === '') {
    return sanitizePositiveIntegerDigits('1', 8)
  }
  const n = parseSliderStepField(current)
  const base = n ?? 1
  return sanitizePositiveIntegerDigits(String(Math.max(1, base + delta)), 8)
}

function mulberry32(seed: number) {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = Math.imul(a ^ (a >>> 15), a | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export function shuffleDeterministic<T>(arr: readonly T[], seedStr: string): T[] {
  const out = [...arr]
  let seed = 0
  for (let i = 0; i < seedStr.length; ) {
    const cp = seedStr.codePointAt(i) ?? 0
    seed = (seed * 31 + cp) >>> 0
    i += cp > 0xffff ? 2 : 1
  }
  const rnd = mulberry32(seed || 1)
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1))
    const a = out[i]
    const b = out[j]
    if (a === undefined || b === undefined) continue
    out[i] = b
    out[j] = a
  }
  return out
}
